import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Button,
  Box,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { connectionService } from '@/api';
import type { Connection } from '../../types';

interface ConnectionCardProps {
  connection: Connection;
  onConnect?: (connection: Connection) => void;
  onDelete?: (connection: Connection) => void;
}

export const ConnectionCard: React.FC<ConnectionCardProps> = ({
  connection,
  onConnect,
  onDelete,
}) => {
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const handleConnect = async (payment_gateway_uuid: string | undefined, payment_flow: string | undefined) => {
    if (!payment_gateway_uuid) {
      toast.error('Payment gateway UUID is required', {
        style: { background: '#d32f2f', color: '#fff' },
      });
      return;
    }

    try {
      setConnecting(true);
      if (payment_flow === 'dedicated_merchant') {
        // Navigate to connection setup page based on payment gateway name using state (not query params)
        if (connection.payment_gateway_name) {
          const setupUrl = `/merchant/connections/setup/${encodeURIComponent(connection.payment_gateway_name)}`;
          navigate(setupUrl, {
            state: { payment_gateway_uuid },
          });
        } else {
          toast.error('Payment gateway name not available', {
            style: { background: '#d32f2f', color: '#fff' },
          });
        }
      } else {
        // Call API to onboard payment gateway
        const response = await connectionService.connect(payment_gateway_uuid);
        if (response.success && response.data?.redirect_url) {
          window.location.href = response.data.redirect_url;
        } else if (response.success) {
          toast.success(response.message || 'Payment gateway connected successfully!', {
            duration: 4000,
            style: { background: '#43a047', color: '#fff' },
          });
          onConnect?.(connection);
        } else {
          toast.error(response.message || 'Failed to connect payment gateway', {
            style: { background: '#d32f2f', color: '#fff' },
          });
        }
      }
    } catch (error: any) {
      toast.error(error?.message || 'An error occurred while connecting', {
        style: { background: '#d32f2f', color: '#fff' },
      });
    } finally {
      setConnecting(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    const connectionId = connection.payment_gateway_uuid;

    if (!connectionId) {
      toast.error('Connection ID not found', {
        style: { background: '#d32f2f', color: '#fff' },
      });
      setDeleteDialogOpen(false);
      return;
    }

    try {
      setDeleting(true);
      const response = await connectionService.deleteConnection(connectionId);
      if (response.success) {
        toast.success(response.message || 'Connection deleted successfully!', {
          duration: 4000,
          style: { background: '#43a047', color: '#fff' },
        });
        onDelete?.(connection);
      } else {
        toast.success(response.message || 'Failed to delete connection', {
          style: { background: '#43a047', color: '#fff' },
        });
        onDelete?.(connection);
      }
    } catch (error: any) {
      toast.error(error?.message || 'An error occurred while deleting connection', {
        style: { background: '#d32f2f', color: '#fff' },
      });
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid rgb(236, 239, 242)',
        background: 'rgb(255, 255, 255)',
        borderRadius: 2,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: 3,
          //transform: 'translateY(-2px)',
          border: '2px solid',
          borderColor: 'primary.main',

        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 2, display: 'flex', flexDirection: 'column' }}>
        {/* Top Section: Icon and Name */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 'auto' }}>
          {/* Icon with gradient background */}
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 1.5,
              //              background: 'linear-gradient(135deg, #4caf50 0%, #2196f3 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <img
              src={connection.payment_gateway_logo}
              alt={connection.payment_gateway_logo}
              style={{ width: 24, height: 24, objectFit: 'contain' }}
            />
          </Box>

          {/* Name */}
          <Typography
            variant="h6"
            component="h3"
            sx={{
              fontWeight: 600,
              fontSize: '1rem',
              color: 'text.primary',
            }}
          >
            {connection.payment_gateway_label}
          </Typography>
        </Box>

        {/* Bottom Section: Type Chip, Delete Icon, and Connect Button */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
          {/* Left side: Status indicator and Delete icon */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Status Indicator */}
            <Chip
              label=""
              size="small"
              sx={{
                width: 15,
                height: 15,
                bgcolor: connection.is_active ? '#03720cff' : '#9e9e9e',
                color: 'text.secondary',
                fontSize: '0.75rem',
                fontWeight: 400,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            />

            {/* Delete Icon - Only show for connected gateways */}
            {connection.is_merchant_onboarded && (
              <IconButton
                size="small"
                onClick={handleDeleteClick}
                sx={{
                  color: 'error.main',
                }}
                aria-label="Delete connection"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

        
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleConnect(connection.payment_gateway_uuid, connection.payment_flow)}
            sx={{
              textTransform: 'none',
              fontWeight: 300,
              fontSize: '13px',
              minWidth: 100,
              borderRadius: 1,
              '&.Mui-disabled': {
                color: 'grey',
              },
            }}
            disabled={connection.is_merchant_onboarded || connection.is_launched === false || connecting}
            startIcon={connecting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {connecting ? 'Connecting...' : connection.is_launched === false ? 'Coming Soon...' : connection.is_merchant_onboarded ? 'Connected' : 'Connect'}
          </Button>
        
        </Box>
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Connection
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the {connection.payment_gateway_label || connection.payment_gateway_name} connection?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="primary"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ConnectionCard;

